/**
 * Page: Verification Registration (Journey-Based)
 *
 * Page de vérification OTP pour registration.
 * Supporte : EMAIL OTP et SMS
 */

import { authContext } from '@/scripts/utils/auth-context.js';
import { alert } from '@/scripts/utils/alert-message.js';
import { KratosFlows } from '@/scripts/api/ory-client.js';
import { initOTPInput } from '@/_includes/components/otp-input.js';

document.addEventListener('DOMContentLoaded', async () => {
  await authContext.init();

  // Initialiser le composant OTP
  initOTPInput();

  const currentJourney = authContext.getCurrentJourney();
  const currentState = authContext.getCurrentState();

  // ============================================
  // VALIDATION (Journey Step Pattern)
  // ============================================
  // Vérifier UNIQUEMENT journey.name + state.name (PAS les données)
  const isExpectedJourneyAndState =
    (currentJourney?.name === 'REGISTRATION_EMAIL_OTP' && currentState?.name === 'otp_verification') ||
    (currentJourney?.name === 'REGISTRATION_SMS' && currentState?.name === 'phone_verification');

  if (!isExpectedJourneyAndState) {
    // ❌ Pas le bon journey/state → CLEAR + REDIRIGER
    authContext.clearJourney();

    alert.error('#alert-container', 'Session expirée. Redirection...', {
      icon: true,
      closable: false,
      duration: 1500
    });

    setTimeout(() => {
      window.location.href = '/registration.html';
    }, 1500);

    return; // Bloquer la page
  }

  // ✅ Bon journey + bon state → Afficher l'UI et setup listeners
  // La validation des données (identifier, flowId) est faite par journey.isValid()

  console.log('[Verification Registration] Valid journey:', {
    journey: currentJourney.name,
    state: currentState.name,
    method: authContext.get('method')
  });

  // UI Setup
  const identifier = authContext.get('identifier');
  const method = authContext.get('method');

  const identifierDisplay = document.querySelector('.identifier-display');
  if (identifierDisplay) {
    identifierDisplay.textContent = identifier;
  }

  const methodText = method === 'email' ? 'e-mail' : 'SMS';
  const instructionText = document.querySelector('.instruction-text');
  if (instructionText) {
    instructionText.textContent = `Nous avons envoyé un code de vérification à 6 chiffres par ${methodText}.`;
  }

  // ============================================
  // TIMER (60 secondes avant affichage du bouton "Renvoyer")
  // ============================================
  const timerElement = document.querySelector('[data-timer]');
  const timerSeconds = document.querySelector('[data-timer-seconds]');
  const resendBtn = document.querySelector('[data-resend-code]');

  let countdown = 60;
  const timerInterval = setInterval(() => {
    countdown--;
    if (timerSeconds) timerSeconds.textContent = countdown;

    if (countdown <= 0) {
      clearInterval(timerInterval);
      if (timerElement) timerElement.classList.add('hidden');
      if (resendBtn) resendBtn.classList.remove('hidden');
    }
  }, 1000);

  // ============================================
  // FORM SUBMISSION
  // ============================================
  // Form Submission
  const form = document.querySelector('form');
  const submitButton = form?.querySelector('button[type="submit"]');
  const codeInput = form?.querySelector('[data-otp-value]'); // Input hidden contenant le code

  if (form && submitButton && codeInput) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      submitButton.disabled = true;
      submitButton.textContent = 'Vérification...';

      try {
        const code = codeInput.value;

        if (!code || code.length !== 6) {
          alert.error('#alert-container', 'Veuillez saisir un code à 6 chiffres.', { icon: true, closable: true });
          submitButton.disabled = false;
          submitButton.textContent = 'Vérifier';
          return;
        }

        authContext.save('code', code);
        await authContext.executeCurrentState();

      } catch (error) {
        console.error('[Verification Registration] Error verifying code:', error);
        submitButton.disabled = false;
        submitButton.textContent = 'Vérifier';
      }
    });
  }

  // ============================================
  // RESEND CODE
  // ============================================
  if (resendBtn) {
    resendBtn.addEventListener('click', async () => {
      resendBtn.disabled = true;
      const originalText = resendBtn.textContent;
      resendBtn.textContent = 'Envoi en cours...';

      try {
        const flow = await KratosFlows.initRegistrationFlow();
        const csrfToken = flow.ui.nodes.find(n => n.attributes.name === 'csrf_token')?.attributes.value;

        // Utiliser les données du contexte
        const firstName = authContext.get('defaultFirstName');

        try {
          await KratosFlows.submitFlow(flow.id, 'registration', {
            method: 'code',
            traits: {
              email: identifier,
              name: {
                first: firstName
              }
            },
            csrf_token: csrfToken,
          });
        } catch (submitError) {
          const responseData = submitError.response?.data;
          const isCodeSent = submitError.response?.status === 400 &&
            (responseData?.ui?.messages?.some(m => m.type === 'info') || responseData?.state === 'sent_email');

          if (!isCodeSent) throw submitError;
        }

        // Mettre à jour avec le nouveau flowId et csrfToken pour le prochain code OTP
        authContext.save('flow', flow.id);
        authContext.save('csrfToken', csrfToken);

        alert.success('#alert-container', 'Un nouveau code a été envoyé.', { icon: true, closable: true, duration: 5000 });

        // Réinitialiser le timer
        resendBtn.classList.add('hidden');
        if (timerElement) timerElement.classList.remove('hidden');
        countdown = 60;
        if (timerSeconds) timerSeconds.textContent = countdown;

        const newTimerInterval = setInterval(() => {
          countdown--;
          if (timerSeconds) timerSeconds.textContent = countdown;

          if (countdown <= 0) {
            clearInterval(newTimerInterval);
            if (timerElement) timerElement.classList.add('hidden');
            if (resendBtn) resendBtn.classList.remove('hidden');
          }
        }, 1000);

      } catch (error) {
        console.error('[Verification Registration] Error resending code:', error);

        const errorMsg = error.response?.data?.ui?.messages?.find(m => m.type === 'error')?.text
          || 'Erreur lors de l\'envoi du code.';

        alert.error('#alert-container', errorMsg, { icon: true, closable: true });

        resendBtn.disabled = false;
        resendBtn.textContent = originalText;
      }
    });
  }
});
